/**
 * Familiene i systemadminpanelet (FF-11, #179): lista med navn, kode,
 * status, antall medlemmer, opprettet og sist aktiv — tall og nøkler, aldri
 * familieinnhold (D3). «Ny familie» oppretter en familie og viser
 * invitasjonslenken til den første adminen én gang.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { SystemFamily, SystemFamilyCreated } from '@stell/shared';
import { Button, Card, Pill } from '../../components/primitives';
import type { PillVariant } from '../../components/primitives';
import { CreateFamilyForm } from './CreateFamilyForm';
import { FamilyActions } from './FamilyActions';
import { OneTimeLink } from './OneTimeLink';
import { useReauthGate } from './SystemReauthDialog';
import { SYSTEM_FAMILIES_KEY, SystemApi } from './systemApi';

const STATUS_PILL: Record<SystemFamily['status'], PillVariant> = {
  active: 'ok',
  pending: 'warn',
  suspended: 'bad',
};

function date(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

export function SystemFamiliesPanel(): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { guard, dialog } = useReauthGate();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<SystemFamilyCreated | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const families = useQuery({ queryKey: SYSTEM_FAMILIES_KEY, queryFn: SystemApi.families });

  const refresh = () => void qc.invalidateQueries({ queryKey: SYSTEM_FAMILIES_KEY });

  return (
    <section data-testid="system-families" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="m-0 text-heading-m font-extrabold text-ink">{t('system.families.title')}</h2>
        {!creating ? (
          <Button data-testid="system-families-new" onClick={() => { setCreated(null); setCreating(true); }}>
            {t('system.families.new')}
          </Button>
        ) : null}
      </div>

      {creating ? (
        <Card>
          <CreateFamilyForm
            guard={guard}
            onCancel={() => setCreating(false)}
            onCreated={(result) => {
              setCreating(false);
              setCreated(result);
              refresh();
            }}
          />
        </Card>
      ) : null}

      {created ? (
        <OneTimeLink link={created.link} adminName={created.admin.name} onDone={() => setCreated(null)} />
      ) : null}

      {families.isPending ? (
        <p className="m-0 text-sm text-ink-2">{t('system.loading')}</p>
      ) : families.isError ? (
        <p role="alert" className="m-0 text-sm text-bad">{t('system.errors.generic')}</p>
      ) : families.data.families.length === 0 ? (
        <p data-testid="system-families-empty" className="m-0 text-sm text-ink-2">{t('system.families.empty')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {families.data.families.map((f) => (
            <li key={f.id}>
              <Card>
                <div data-testid={`system-family-${f.code}`} className="flex flex-col gap-3">
                  <button
                    type="button"
                    aria-expanded={open === f.id}
                    data-testid={`system-family-toggle-${f.code}`}
                    className="flex w-full flex-wrap items-center justify-between gap-2 text-left"
                    onClick={() => setOpen(open === f.id ? null : f.id)}
                  >
                    <span className="flex flex-col">
                      <span className="text-body font-extrabold text-ink">{f.name}</span>
                      <span className="text-sm text-ink-2 tnum">{f.code}</span>
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      {f.setup_pending ? <Pill variant="lilac">{t('system.families.setup_pending')}</Pill> : null}
                      <Pill variant={STATUS_PILL[f.status]}>{t(`system.status.${f.status}`)}</Pill>
                    </span>
                  </button>
                  <dl className="m-0 grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <dt className="text-ink-2">{t('system.families.members')}</dt>
                      <dd className="m-0 font-extrabold text-ink tnum">{f.member_count}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-2">{t('system.families.created')}</dt>
                      <dd className="m-0 text-ink">{date(f.created_at)}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-2">{t('system.families.last_active')}</dt>
                      <dd className="m-0 text-ink">{date(f.last_active_at)}</dd>
                    </div>
                  </dl>
                  {open === f.id ? (
                    <FamilyActions
                      family={f}
                      guard={guard}
                      onChanged={refresh}
                      onDeleted={() => {
                        setOpen(null);
                        refresh();
                      }}
                    />
                  ) : null}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
      {dialog}
    </section>
  );
}
